import type {
  DefaultError,
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import {
  // oxlint-disable-next-line custom/restrict-named-imports
  useInfiniteQuery as useBaseInfiniteQuery,
} from "@tanstack/react-query";

interface ItemsPage<Item> {
  readonly items: readonly Item[];
}

export type InfiniteQueryResult<Item, TData, TError> = {
  readonly items: readonly Item[];
  readonly loadNextPage: () => void;
} & UseInfiniteQueryResult<TData, TError>;

export function useInfiniteQuery<
  Page extends ItemsPage<unknown>,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<
    Page,
    TError,
    InfiniteData<Page>,
    TQueryKey,
    TPageParam
  >,
): InfiniteQueryResult<Page["items"][number], InfiniteData<Page>, TError> {
  const query = useBaseInfiniteQuery(options);

  const items = query.data?.pages.flatMap(page => page.items) ?? [];

  const loadNextPage = (): void => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      // oxlint-disable-next-line typescript/no-floating-promises
      query.fetchNextPage();
    }
  };

  return Object.assign(query, { items, loadNextPage });
}
